<template>
  <div class="collection-hero">
    <div class="collection-hero__media">
      <art-direction
        :image="image"
        :lazyload=false
        :image-desktop="image"
        ratio="4:3"
        ratio-desktop="23:9"
      />

      <div class="collection-hero__media-overlay" />
    </div>
    <div class="collection-hero__content">
      <nav
        class="collection-hero__breadcrumb"
        role="navigation"
        :aria-label="$string('collection.all_products')"
      >
        <a v-if="bladefilter"
          :href="navurl"
          :title="$string('collection.all_products')"
          class="
                text-other-button-s-underline
                breadcrumb__item
                breadcrumb__link
              "
          v-text="navtitle"
        />
        <a v-else
          href="/products"
          :title="$string('collection.all_products')"
          class="
                text-other-button-s-underline
                breadcrumb__item
                breadcrumb__link
              "
          v-text="$string('collection.all_products')"
        />

        <span
          aria-hidden="true"
          class="collection-hero__breadcrumb-separator"
        >/</span>

        <span
          class="breadcrumb__item text-heading-s"
          v-html="title"
        />
      </nav>

      <h1
        id="template-title"
        class="
              text-heading-3xl
              text-heading-5xl-desktop
              collection-hero__title
              mt-m
              mt-l-desktop
            "
        v-html="title"
      />

      <div
        v-if="description"
        class="collection-hero__description col xs-span m4 l6 mt-m mt-l-desktop"
      >
        <div
          class="rte"
          v-html="description"
        />

        <btn
          v-if="buttonUrl"
          :url="buttonUrl"
          class="collection-hero__cta"
          :label="buttonLabel"
          modifiers="primary center"
          type="button"
          hover-only
        >
          <template #hoverIcon>
            <icon-arrow-right />
          </template>
        </btn>
      </div>

      <div
        v-if="Object.keys(filter).length > 0"
        class="collection-hero__filters mt-l mt-xl-desktop"
      >
        <splide
          class="collection-hero__filter-carousel filter-carousel--heroFilter"
          :has-track="false"
          :options="localConfig"
        >
          <splide-track>
            <splide-slide
              v-for="(option, index) of splitOptions"
              :key="`hero-filter-${index}`"
            >
              <btn
                class="collection-hero__filter-btn"
                :class="{'active': selectedOption === option}"
                :label="option"
                modifiers="primary block center"
                name="filters"
                type="button"
                @click="setOptions(option)"
              >
                <template #icon>
                  <icon-check />
                </template>
              </btn>
            </splide-slide>
          </splide-track>
        </splide>
      </div>
    </div>
  </div>
</template>

<script>

import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import IconCheck from '~icons/general/check.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import { mapGetters } from 'vuex'

/**
 * Vue: Collection hero (collection-hero)
 * -----------------------------------------------------------------------------
 * Collection hero banner.
 *
 */
export default {
  name: 'CollectionHero',
  components: {
    ArtDirection,
    Btn,
    IconCheck,
    IconArrowRight,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    description: {
      type: String,
      default: null,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    filterManager: {
      type: Object,
      default: () => ({}),
    },
    image: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: null,
    },
    navurl: {
      type: String,
      default: null,
    },
    navtitle: {
      type: String,
      default: null,
    },
    bladefilter:{
      type : Boolean,
      default : false
    },
    buttonUrl: {
      type: String,
      default: null,
    },
    buttonLabel: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selectedOption: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFilters: 'search/getFilters',
    }),

    /**
     * Split model filter options
     * @returns {Array}
     */
    splitOptions() {
      if (!this.filter.options) {
        return
      }

      const options = this.filter.options.reduce((acc, { value }) => {
        const splitValue = value.split('|')
        return [...acc, ...splitValue]
      }, [])

      return [...new Set(options)]
    },
  },
  beforeMount() {
    this.localConfig = {
      arrows: false,
      drag: true,
      fixedWidth: 'auto',
      flickPower: 200,
      flickMaxPages: 1,
      gap: '1rem',
      mediaQuery: 'min',
      pagination: false,
    }
  },

  methods: {

    /**
     * Set selected options.
     * @param {String} value - Selected option value.
     */
    setOptions(value) {
      const selected = this.filter.options.filter((option) => option.value.includes(value))
      this.filterManager.clearOptionSelections(this.filter.key)

      if (this.selectedOption === value) {
        this.filter.options.forEach((option) => {
          this.filterManager.deselectOption(this.filter.key, option.value)
        })

        this.selectedOption = ''
        return
      }

      selected.forEach((option) => {
        this.filterManager.selectOption(this.filter.key, option.value)
      })
      this.selectedOption = value
    },

    hasActiveFilter(option) {
      if (!this.getFilters) {
        return
      }
      const modelFilters = this.getFilters.find((filter) => filter.key === 'sawmill_model' || filter.key === 'sawmill_model_new')
      const selectedFilter = modelFilters.options.find((model) => {
        return model.value.includes(option) && model.selected
      })

      return selectedFilter
    },
  },
}
</script>

<style lang="scss">
@import './collection-hero';
</style>
